import type {
  Dispatch,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactElement,
  SetStateAction,
} from "react";
import { useRef, useState } from "react";

import {
  AttachFile as AttachFileIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { ArticleResourceName, ArticleResourceRow } from "./ArticleResourceRow";
import { ArticleUploadStatus } from "./ArticleUploadStatus";
import type { ArticleFormState } from "../types/article-form.types";
import type {
  ArticleAttachment,
  FileUploadStatus,
} from "../types/article.types";
import { getFileSignature, splitFileName } from "../utils/fileUtils";

const LINKED_IMAGE_BASE_NAME_MAX_LENGTH = 80;

type ArticleResourcesSectionProps = {
  autoTags: string[];
  deleteExistingAttachment: (attachmentId: number) => void;
  existingAttachments: ArticleAttachment[];
  existingInlineLinkedImageLabels: Map<number, string>;
  files: File[];
  form: ArticleFormState;
  images: File[];
  inlineLinkedImageLabels: Map<string, string>;
  onRenameLinkedImage: (fileSignature: string, nextName: string) => void;
  removeTag: (tag: string) => void;
  setField: <K extends keyof ArticleFormState>(
    field: K,
  ) => (value: ArticleFormState[K]) => void;
  setFiles: Dispatch<SetStateAction<File[]>>;
  setImages: Dispatch<SetStateAction<File[]>>;
  setTagInput: (value: string) => void;
  tagInput: string;
  addTag: (tag: string) => void;
  uploadStatuses: Record<string, FileUploadStatus>;
};

export function ArticleResourcesSection({
  autoTags,
  deleteExistingAttachment,
  existingAttachments,
  existingInlineLinkedImageLabels,
  files,
  form,
  images,
  inlineLinkedImageLabels,
  onRenameLinkedImage,
  removeTag,
  setField,
  setFiles,
  setImages,
  setTagInput,
  tagInput,
  addTag,
  uploadStatuses,
}: ArticleResourcesSectionProps) {
  const [editingImageSignature, setEditingImageSignature] = useState("");
  const [editingImageName, setEditingImageName] = useState("");

  const renameNameLength = editingImageName.trim().length;
  const isRenameTooLong = renameNameLength > LINKED_IMAGE_BASE_NAME_MAX_LENGTH;
  const renameHelperText = isRenameTooLong
    ? `Name must be ${LINKED_IMAGE_BASE_NAME_MAX_LENGTH} characters or fewer. Currently ${renameNameLength}.`
    : `Name only, without extension. ${renameNameLength}/${LINKED_IMAGE_BASE_NAME_MAX_LENGTH}`;
  const existingImages = existingAttachments.filter((attachment) =>
    attachment.mime_type.startsWith("image/"),
  );
  const existingFiles = existingAttachments.filter(
    (attachment) => !attachment.mime_type.startsWith("image/"),
  );

  function handleAddTag(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" && tagInput.trim()) {
      event.preventDefault();
      addTag(tagInput);
    }
  }

  function startImageRename(file: File) {
    setEditingImageSignature(getFileSignature(file));
    setEditingImageName(splitFileName(file.name).baseName);
  }

  function cancelImageRename() {
    setEditingImageSignature("");
    setEditingImageName("");
  }

  function saveImageRename(originalFileName?: string) {
    if (!editingImageSignature || !editingImageName.trim()) {
      return;
    }

    const extension = originalFileName
      ? splitFileName(originalFileName).extension
      : "";
    onRenameLinkedImage(
      editingImageSignature,
      `${editingImageName.trim()}${extension}`,
    );
    cancelImageRename();
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" color="primary" mb={0.5}>
        4. Files and References
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Support evidence stored in the document library and referenced from
        Wiki.js.
      </Typography>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
        gap={2}
      >
        <Box>
          <Typography variant="subtitle2" mb={1}>
            Images
          </Typography>
          <UploadDropzone
            buttonLabel="Choose Images"
            caption="Screenshots, redlines, diagrams, or photos"
            helperText="Large JPEG, PNG, and WebP images are compressed before S3 upload."
            icon={<ImageIcon />}
            inputProps={{ accept: "image/*" }}
            onFilesSelected={(selectedFiles) =>
              setImages((current) => [...current, ...selectedFiles])
            }
          />

          {images.length > 0 && (
            <Box mt={1} display="flex" flexDirection="column" gap={0.5}>
              {images.map((image, index) => {
                const fileSignature = getFileSignature(image);
                const inlineLinkedLabel =
                  inlineLinkedImageLabels.get(fileSignature);
                const isInlineLinked = Boolean(inlineLinkedLabel);
                const isEditing = editingImageSignature === fileSignature;
                const { extension } = splitFileName(image.name);

                return (
                  <ArticleResourceRow
                    key={`${image.name}-${index}`}
                    actions={
                      <Box display="flex" alignItems="center">
                        {isInlineLinked &&
                          (isEditing ? (
                            <>
                              <Tooltip title="Save name">
                                <span>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "success.main" }}
                                    onClick={() => saveImageRename(image.name)}
                                    disabled={
                                      !editingImageName.trim() ||
                                      isRenameTooLong
                                    }
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Cancel rename">
                                <IconButton
                                  size="small"
                                  sx={{ color: "error.main" }}
                                  onClick={cancelImageRename}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Rename linked image">
                              <IconButton
                                size="small"
                                onClick={() => startImageRename(image)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ))}
                        <Tooltip
                          title={
                            isInlineLinked
                              ? "Delete this image from the rich text editor to remove it here."
                              : "Remove image on save"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={isInlineLinked}
                              onClick={() =>
                                setImages((current) =>
                                  current.filter(
                                    (_, currentIndex) => currentIndex !== index,
                                  ),
                                )
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={0.75}
                      flex={1}
                      minWidth={0}
                    >
                      {isEditing ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <TextField
                            size="small"
                            value={editingImageName}
                            onChange={(event) =>
                              setEditingImageName(event.target.value)
                            }
                            error={isRenameTooLong}
                            helperText={renameHelperText}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                if (!isRenameTooLong) {
                                  saveImageRename(image.name);
                                }
                              }

                              if (event.key === "Escape") {
                                event.preventDefault();
                                cancelImageRename();
                              }
                            }}
                            sx={{ minWidth: 220 }}
                          />
                          {extension && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {extension}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <ArticleResourceName>{image.name}</ArticleResourceName>
                      )}
                      {inlineLinkedLabel && (
                        <Box>
                          <Chip
                            size="small"
                            label={inlineLinkedLabel}
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      )}
                      <ArticleUploadStatus
                        file={image}
                        uploadStatuses={uploadStatuses}
                      />
                    </Box>
                  </ArticleResourceRow>
                );
              })}
            </Box>
          )}

          {existingImages.length > 0 && (
            <Box mt={1} display="flex" flexDirection="column" gap={0.5}>
              {existingImages.map((image) => {
                const inlineLinkedLabel = existingInlineLinkedImageLabels.get(
                  image.id,
                );
                const isInlineLinked = Boolean(inlineLinkedLabel);

                return (
                  <ArticleResourceRow
                    key={`existing-image-${image.id}`}
                    actions={
                      <Tooltip
                        title={
                          isInlineLinked
                            ? "Delete this image from the rich text editor to remove it here."
                            : "Remove image on save"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={isInlineLinked}
                            onClick={() => deleteExistingAttachment(image.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    }
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={0.75}
                      flex={1}
                      minWidth={0}
                    >
                      <ArticleResourceName>
                        {image.original_name}
                      </ArticleResourceName>
                      <Box display="flex" gap={0.75} flexWrap="wrap">
                        <Chip
                          size="small"
                          label="Already attached"
                          variant="outlined"
                        />
                        {inlineLinkedLabel && (
                          <Chip
                            size="small"
                            label={inlineLinkedLabel}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  </ArticleResourceRow>
                );
              })}
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" mb={1}>
            Attachments
          </Typography>
          <UploadDropzone
            buttonLabel="Choose Files"
            caption="Firmware, manuals, drawings, or tools"
            icon={<AttachFileIcon />}
            onFilesSelected={(selectedFiles) =>
              setFiles((current) => [...current, ...selectedFiles])
            }
          />

          {files.length > 0 && (
            <Box mt={1} display="flex" flexDirection="column" gap={0.5}>
              {files.map((file, index) => (
                <ArticleResourceRow
                  key={`${file.name}-${index}`}
                  actions={
                    <IconButton
                      size="small"
                      onClick={() =>
                        setFiles((current) =>
                          current.filter(
                            (_, currentIndex) => currentIndex !== index,
                          ),
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={0.5}
                    minWidth={0}
                  >
                    <ArticleResourceName>{file.name}</ArticleResourceName>
                    <ArticleUploadStatus
                      file={file}
                      uploadStatuses={uploadStatuses}
                    />
                  </Box>
                </ArticleResourceRow>
              ))}
            </Box>
          )}

          {existingFiles.length > 0 && (
            <Box mt={1} display="flex" flexDirection="column" gap={0.5}>
              {existingFiles.map((file) => (
                <ArticleResourceRow
                  key={`existing-file-${file.id}`}
                  actions={
                    <Tooltip title="Remove file on save">
                      <IconButton
                        size="small"
                        onClick={() => deleteExistingAttachment(file.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <Box
                    display="flex"
                    gap={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    minWidth={0}
                  >
                    <ArticleResourceName>
                      {file.original_name}
                    </ArticleResourceName>
                    <Chip
                      size="small"
                      label="Already attached"
                      variant="outlined"
                    />
                  </Box>
                </ArticleResourceRow>
              ))}
            </Box>
          )}
        </Box>

        <TextField
          label="External Links"
          fullWidth
          multiline
          rows={3}
          value={form.external_links}
          onChange={(event) => setField("external_links")(event.target.value)}
          placeholder="Paste one or more SharePoint or external reference links."
          helperText="Add one link per line. Plain https:// URLs and Markdown links like [Label](https://example.com) are supported."
        />

        <Box>
          <TextField
            label="Keywords / Tags"
            fullWidth
            value={tagInput}
            onChange={(event) => setTagInput(event.target.value)}
            onKeyDown={handleAddTag}
            placeholder="Example: PLS, Gemco 7500, Firmware Flash - press Enter to add"
            helperText="Some tags are auto-generated from selected fields"
          />
          {(form.tags.length > 0 || autoTags.length > 0) && (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              mt={1}
              useFlexGap
            >
              {form.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  onDelete={() => removeTag(tag)}
                />
              ))}
              {autoTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

type UploadDropzoneProps = {
  buttonLabel: string;
  caption: string;
  helperText?: string;
  icon: ReactElement;
  inputProps?: Pick<InputHTMLAttributes<HTMLInputElement>, "accept">;
  onFilesSelected: (files: File[]) => void;
};

function UploadDropzone({
  buttonLabel,
  caption,
  helperText,
  icon,
  inputProps,
  onFilesSelected,
}: UploadDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => fileInputRef.current?.click()}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          fileInputRef.current?.click();
        }
      }}
      sx={{
        display: "block",
        width: "100%",
        border: "2px dashed #9fc3df",
        bgcolor: "#f8fbfe",
        borderRadius: 2,
        p: 2.5,
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      <Button variant="outlined" startIcon={icon} size="small">
        {buttonLabel}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept={inputProps?.accept}
        onChange={(event) =>
          onFilesSelected(Array.from(event.target.files || []))
        }
      />
      <Typography
        variant="caption"
        display="block"
        color="text.secondary"
        mt={1}
      >
        {caption}
      </Typography>
      {helperText && (
        <Typography
          variant="caption"
          display="block"
          color="text.secondary"
          mt={0.5}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
